import { faChevronRight, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { Col, FloatingLabel, Form, Row } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";
import AlertBox from "../../../components/Messages";
import { useCountryArr } from "../../../hooks/useCountryArr";
import titanLogo from "../../../images/Ischebeck_Logo2013_VarA_RGB_1zu1_trans.svg";
import { useRegisterMutation } from "../../../services/API/titanApi";
import BasePage from "../../BasePage/BasePage";
import Success from "../Popup/Success";
import RegisterBubble from "../RegisterBubble/RegisterBubble";
import FormEntry, { ErrorText } from "./FormEntry";
import "./Register.scss";

function Register({ initialLang }) {
  const { i18n, t } = useTranslation(["translation", "register"]);
  const location = useLocation();
  const navigate = useNavigate();

  const countryArr = useCountryArr();

  const initialState = {
    agb: false,
    username: "",
    name: "",
    surname: "",
    email: "",
    phone: "",
    companyName: "",
    city: "",
    address: "",
    zipCode: "",
    country: "",
    password: "",
    passwordConfirmation: "",
    salutation: "",
    title: "",
  };
  const [formState, setFormState] = useState(initialState);
  const [errMsg, setErrMsg] = useState("");
  const [register, { isLoading, error }] = useRegisterMutation();
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = ({ target: { name, value } }) => {
    return setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeCheckbox = ({ target: { name, checked } }) => {
    return setFormState((prev) => ({ ...prev, [name]: checked }));
  };

  function closeModal() {
    setIsSuccess(false);
    setFormState(initialState);
    setErrMsg("");
  }

  function submitModal() {
    navigate("/");
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (isLoading) {
      return;
    }

    if (!formState.agb) {
      setErrMsg(t("error.missingTermsAndConditions", { ns: "register" }));
      return;
    }

    if (formState.password !== formState.passwordConfirmation) {
      setErrMsg(t("error.noPasswordMatch", { ns: "register" }));
      return;
    }

    try {
      await register(formState).unwrap();
      setIsSuccess(true);
      setFormState(initialState);
    } catch (err) {
      if (err?.status === 400) {
        const serverErrors = Array.isArray(err?.data?.errors) ? err.data.errors : [];
        const mapsToFormField = serverErrors.some((e) =>
          Object.prototype.hasOwnProperty.call(initialState, e?.path ?? e?.param)
        );
        if (mapsToFormField) {
          // field-level messages are rendered at the inputs via getError
          return;
        }
      }
      setErrMsg(t("error.failed", { ns: "register" }));
    }
  }

  useEffect(() => {
    setErrMsg("");
  }, [formState]);

  const [langWasModified, setlangWasModified] = useState(false);

  // Set language on mount
  useEffect(() => {
    if (!langWasModified) {
      i18n.changeLanguage(initialLang).then(() => {
        setlangWasModified(true);
      });
    }
  }, [initialLang, i18n, langWasModified]);

  // Update URL when language changes
  useEffect(() => {
    if (langWasModified) {
      let route = "/registrieren"; // default German
      if (i18n.language === "en") {
        route = "/register";
      } else if (i18n.language === "es-ES") {
        route = "/registrese";
      }
      if (location.pathname !== route) {
        navigate(route, { replace: true });
      }
    }
  }, [i18n.language, navigate, location.pathname, langWasModified]);

  function getError(errorField) {
    return error?.data?.errors?.find((item) => {
      // express-validator v7 reports the field name as "path" ("param" was v6)
      return (item.path ?? item.param) === errorField;
    });
  }

  return (
    <BasePage size={8} bubble={<RegisterBubble />}>
      <div className="register-top-logo-wrapper">
        <img className="register-top-logo" src={titanLogo} alt="" />
      </div>
      <Success show={isSuccess} handleClose={closeModal} handleSubmit={submitModal} />
      <form className="row g-0" onSubmit={handleSubmit}>
        <div className="col-12">
          <div className="pt-md-5 px-5 mx-md-4">
            <h1 className="mt-2 pb-2 register-headline">{t("register.title")}</h1>
            <h2 className="mb-5 pb-1 register-h2">{t("register.thisSite")}</h2>
          </div>
        </div>

        <div className="col-8">
          <div className="px-5 mx-4 mb-5">
            <FormEntry
              name={"companyName"}
              handleChange={handleChange}
              isInvalid={getError}
              value={formState.companyName}
              labelText={t("labelText.companyName", {
                ns: "register",
              })}
            />

            <Row>
              <Col md={6}>
                <FloatingLabel
                  className="mb-md-3"
                  controlId="InputSalutation"
                  label={t("labelText.salutation", {
                    ns: "register",
                  })}
                >
                  <Form.Select
                    name="salutation"
                    value={formState.salutation}
                    onChange={handleChange}
                    className={classNames("register-form-control", {
                      "is-invalid": getError("salutation"),
                    })}
                    required
                  >
                    <option key={0} value={""}>
                      {t("select")}
                    </option>
                    <option key={1} value={"male"}>
                      {t("salutation.male", {
                        ns: "register",
                      })}
                    </option>
                    <option key={2} value={"female"}>
                      {t("salutation.female", {
                        ns: "register",
                      })}
                    </option>
                    <option key={3} value={"diverse"}>
                      {t("salutation.diverse", {
                        ns: "register",
                      })}
                    </option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid" tooltip>
                    <ErrorText name={"salutation"} isInvalid={getError} />
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
              <Col md={6}>
                <FormEntry
                  name={"title"}
                  handleChange={handleChange}
                  isInvalid={getError}
                  value={formState.title}
                  required={false}
                  labelText={t("labelText.title", {
                    ns: "register",
                  })}
                />
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormEntry
                  name={"name"}
                  handleChange={handleChange}
                  isInvalid={getError}
                  value={formState.name}
                  labelText={t("labelText.name", {
                    ns: "register",
                  })}
                />
              </Col>
              <Col md={6}>
                <FormEntry
                  name={"surname"}
                  handleChange={handleChange}
                  isInvalid={getError}
                  value={formState.surname}
                  labelText={t("labelText.surname", {
                    ns: "register",
                  })}
                />
              </Col>
            </Row>

            <FormEntry
              name={"address"}
              handleChange={handleChange}
              isInvalid={getError}
              value={formState.address}
              labelText={t("labelText.address", {
                ns: "register",
              })}
            />

            <Row>
              <Col md={6}>
                <FormEntry
                  name={"zipCode"}
                  handleChange={handleChange}
                  isInvalid={getError}
                  value={formState.zipCode}
                  labelText={t("labelText.zipCode", {
                    ns: "register",
                  })}
                />
              </Col>
              <Col md={6}>
                <FormEntry
                  name={"city"}
                  handleChange={handleChange}
                  isInvalid={getError}
                  value={formState.city}
                  labelText={t("labelText.city", {
                    ns: "register",
                  })}
                />
              </Col>
            </Row>

            <FloatingLabel
              className="mb-md-3"
              controlId="InputCountry"
              label={t("labelText.country", {
                ns: "register",
              })}
            >
              <Form.Select
                name="country"
                value={formState.country}
                onChange={handleChange}
                className={classNames("register-form-control", {
                  "is-invalid": getError("country"),
                })}
                required
              >
                <option value="">{t("select")}</option>

                {!!countryArr?.length &&
                  countryArr.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid" tooltip>
                <ErrorText name={"country"} isInvalid={getError} />
              </Form.Control.Feedback>
            </FloatingLabel>

            <Row>
              <Col md={6}>
                <FormEntry
                  name={"phone"}
                  handleChange={handleChange}
                  isInvalid={getError}
                  value={formState.phone}
                  labelText={t("labelText.phone", {
                    ns: "register",
                  })}
                />
              </Col>
              <Col md={6}>
                <FormEntry
                  name={"email"}
                  handleChange={handleChange}
                  isInvalid={getError}
                  value={formState.email}
                  labelText={t("labelText.email", {
                    ns: "register",
                  })}
                />
              </Col>
            </Row>

            <FormEntry
              name={"username"}
              handleChange={handleChange}
              isInvalid={getError}
              value={formState.username}
              labelText={t("labelText.username", {
                ns: "register",
              })}
            />

            <FormEntry
              name={"password"}
              type={"password"}
              handleChange={handleChange}
              isInvalid={getError}
              value={formState.password}
              labelText={t("labelText.password", {
                ns: "register",
              })}
            />

            <FormEntry
              name={"passwordConfirmation"}
              type={"password"}
              handleChange={handleChange}
              isInvalid={getError}
              value={formState.passwordConfirmation}
              labelText={t("labelText.passwordConfirmation", {
                ns: "register",
              })}
            />

            <div className="pt-1 my-5 pb-1">
              <button name="registerButton" type="submit" className="login-button">
                <div className="verticallyAligned">
                  <div>{t("register.title")}</div>
                  <div style={{ marginLeft: "25px" }}>
                    {isLoading ? (
                      <FontAwesomeIcon color="yellow" icon={faSpinner} className={"fa-spin"} />
                    ) : (
                      <FontAwesomeIcon color="yellow" icon={faChevronRight} />
                    )}
                  </div>
                </div>
              </button>
              <AlertBox show={errMsg !== ""} message={errMsg} />
            </div>
            <br />
            <div>
              <Link className="login-linkText" to="/">
                {t("register.login")}
              </Link>
            </div>
          </div>
        </div>

        <div className="col-4">
          <div className="mx-md-4">
            <div className="register-page checkboxes">
              <label>
                <div className="mb-md-3">
                  <input
                    type="checkbox"
                    name="agb"
                    onClick={handleChangeCheckbox}
                    value={formState.agb}
                    required
                  />

                  <span className="mt-3 px-2" style={{ fontFamily: "Arial" }}>
                    <Trans
                      i18nKey="register.termsAndService_1"
                      values={{
                        nutzungsbedingungen: `<a target="_blank" href='${"/termsofuse"}'>${t(
                          "register.nutzungsbedingungen"
                        )}</a>`,
                        datenschutz: `<a target="_blank" href='${
                          i18n.language === "de-DE"
                            ? "https://www.ischebeck.de/datenschutzerklaerung/"
                            : "https://www.ischebeck.de/en/privacy-policy/"
                        }'>${t("register.datenschutz")}</a>`,
                      }}
                      components={{ a: <a /> }}
                    />
                  </span>
                </div>
              </label>
            </div>
          </div>
        </div>
      </form>
    </BasePage>
  );
}

export default Register;
